/* Strike/dip serialization.
 *
 * The PRD stores `strike_dip` as TEXT and says, verbatim, "or trend/plunge,
 * format TBD with geology team". So the format is unconfirmed and lives behind
 * these two functions. When the team names one, change them and nothing else in
 * either app moves.
 *
 * Current guess: right-hand-rule strike, three digits, then dip, two digits,
 * then dip quadrant — "218/64SE". This is the most common field shorthand and
 * matches the example in the architecture spec's demo script.
 */

import type { StructuralFeature } from './types';

export const READING_FORMAT_CONFIRMED = false;

export const READING_FORMAT_NOTE =
  'Strike/dip text format is a best guess (218/64SE) — PRD leaves it TBD with the geology team.';

export interface Reading {
  strike: number;
  dip: number;
  dip_direction: string;
}

export const formatReading = (r: Reading): string =>
  `${String(Math.round(r.strike)).padStart(3, '0')}/${String(Math.round(r.dip)).padStart(2, '0')}${r.dip_direction}`;

export function parseReading(text: string): Reading | null {
  const m = /^(\d{1,3})\s*\/\s*(\d{1,2})\s*([NSEW]{1,2})?$/i.exec(text.trim());
  if (!m) return null;
  return {
    strike: Number(m[1]),
    dip: Number(m[2]),
    dip_direction: (m[3] ?? '').toUpperCase(),
  };
}

/**
 * The dip direction implied by right-hand-rule strike — 90° clockwise.
 * Offered as a default only; the geologist can override, because RHR is a
 * convention and conventions get broken in a notebook at 2pm.
 */
export function impliedDipDirection(strike: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const bearing = (strike + 90) % 360;
  return dirs[Math.round(bearing / 45) % 8];
}

/** How a reading reads on screen. Source is always shown with it. */
export const readingLabel = (f: StructuralFeature): string =>
  f.strike_dip || (f.reading ? formatReading(f.reading) : '—');

export const SOURCE_LABEL: Record<string, string> = {
  brunton: 'Brunton',
  sensor: 'Phone sensor',
  estimate: 'Estimate',
};

/** Shown next to any sensor-sourced reading, everywhere it appears. */
export const SOURCE_CAVEAT: Record<string, string | undefined> = {
  sensor: 'Phone magnetometer — unreliable on mineralized ground.',
};
